// ============================================
// INVESTORQ FEATURE GATE COMPONENT (Updated)
// Now includes Options Pro as separate $80/mo subscription
// ============================================

import React, { useState, useEffect } from 'react';
import { 
  Lock, 
  Crown, 
  Zap, 
  Star, 
  ArrowRight, 
  Target,
  Building,
  BarChart3,
  Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// Feature configuration
// 🆕 Options features now require 'options_pro' (separate $80/mo subscription)
const FEATURE_CONFIG = {
  // ========== OPTIONS PRO FEATURES ($80/mo separate subscription) ==========
  'options_chain': { minTier: 'options_pro', name: 'Options Chain', icon: Target },
  'options_strategy': { minTier: 'options_pro', name: 'Strategy Builder', icon: Target },
  'options_screener': { minTier: 'options_pro', name: 'Options Screener', icon: Target },
  'iv_crush': { minTier: 'options_pro', name: 'IV Crush Predictor', icon: Target },
  'greeks_analysis': { minTier: 'options_pro', name: 'Greeks Analysis', icon: Target },
  'options_flow': { minTier: 'options_pro', name: 'Options Flow', icon: Target },
  'gamma_exposure': { minTier: 'options_pro', name: 'Gamma Exposure (GEX)', icon: Target },
'volatility_surface': { minTier: 'options_pro', name: '3D Vol Surface', icon: Target },
'opex_calendar': { minTier: 'options_pro', name: 'OpEx Calendar', icon: Target },
'gex_history': { minTier: 'options_pro', name: 'Historical GEX', icon: Target },
'gex_alerts': { minTier: 'options_pro', name: 'GEX Alerts', icon: Target },
  
  // ========== SMART MONEY (Premium) ==========
  'congress_trades': { minTier: 'premium', name: 'Congress Trades', icon: Building },
  'insider_trading': { minTier: 'premium', name: 'Insider Trading', icon: Building },
  'whale_watcher': { minTier: 'premium', name: 'Whale Watcher', icon: Building },
  
  // ========== PRO FEATURES ($9.99/mo) ==========
  'recession_dashboard': { minTier: 'pro', name: 'Recession Dashboard', icon: Activity },
  'etf_xray': { minTier: 'pro', name: 'ETF X-Ray', icon: BarChart3 },
  'etf_compare': { minTier: 'pro', name: 'ETF Compare', icon: BarChart3 },
  'crypto_deep_dive': { minTier: 'pro', name: 'Crypto Deep Dive', icon: Activity },
  'dca_calculator': { minTier: 'pro', name: 'DCA Calculator', icon: BarChart3 },
  'top_winners': { minTier: 'pro', name: 'AI Top Winners', icon: BarChart3 },
  
  // ========== PREMIUM FEATURES ($19.99/mo) ==========
  'forex_dashboard': { minTier: 'premium', name: 'Forex Dashboard', icon: Activity },
  'portfolio_analytics': { minTier: 'premium', name: 'Advanced Analytics', icon: BarChart3 },
  'portfolio_rebalancer': { minTier: 'premium', name: 'Portfolio Rebalancer', icon: BarChart3 },
  'crypto_correlation': { minTier: 'premium', name: 'Crypto Correlation', icon: Activity },
  'anomaly_detector': { minTier: 'premium', name: 'Anomaly Detector', icon: Activity },
};

// Tier hierarchy (Options Pro is separate, not in hierarchy)
const TIER_LEVEL = { 'free': 0, 'pro': 1, 'premium': 2 };

// Tier colors
const TIER_COLORS = {
  free: { primary: '#60a5fa', bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.3)' },
  pro: { primary: '#a78bfa', bg: 'rgba(139, 92, 246, 0.1)', border: 'rgba(139, 92, 246, 0.3)' },
  premium: { primary: '#fbbf24', bg: 'rgba(251, 191, 36, 0.1)', border: 'rgba(251, 191, 36, 0.3)' },
  options_pro: { primary: '#f97316', bg: 'rgba(249, 115, 22, 0.1)', border: 'rgba(249, 115, 22, 0.3)' }
};

// ============================================
// MAIN FEATURE GATE COMPONENT
// ============================================

export const FeatureGate = ({ feature, children, user }) => {
  const [userTier, setUserTier] = useState('free');
  const [optionsProActive, setOptionsProActive] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserTier();
  }, [user]);

  const fetchUserTier = async () => {
    // Get user ID from props or localStorage
    const userId = user?.id || localStorage.getItem('userId');
    
    if (!userId) {
      setUserTier('free');
      setOptionsProActive(false);
      setLoading(false);
      return;
    }

    try {
      // Fetch main subscription status
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: { 'x-user-id': userId }
      });
      const data = await response.json();
      setUserTier(data.tier || 'free');
      
      // 🆕 Also fetch Options Pro status
      try {
        const optionsResponse = await fetch(`${API_URL}/stripe/subscription-status/${userId}`);
        const optionsData = await optionsResponse.json();
        if (optionsData.success && optionsData.optionsPro) {
          setOptionsProActive(optionsData.optionsPro.active || false);
        }
      } catch (optionsErr) {
        // If options endpoint fails, check user object
        setOptionsProActive(user?.options_pro_active || false);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setUserTier('free');
      setOptionsProActive(false);
    } finally {
      setLoading(false);
    }
  };

  const config = FEATURE_CONFIG[feature];
  const requiredTier = config?.minTier || 'premium';
  
  // 🆕 Check if this is an Options Pro feature
  const isOptionsProFeature = requiredTier === 'options_pro';
  
  // Check if user has access
  let hasAccess = false;
  if (isOptionsProFeature) {
    // Options Pro features require separate subscription
    hasAccess = optionsProActive;
  } else {
    // Regular tier hierarchy check
    hasAccess = TIER_LEVEL[userTier] >= TIER_LEVEL[requiredTier];
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        padding: '3rem' 
      }}>
        <div style={{ color: '#94a3b8' }}>Loading...</div>
      </div>
    );
  }

  // User has access - show the feature
  if (hasAccess) {
    return children;
  }

  // User doesn't have access - show upgrade prompt
  return <UpgradePrompt feature={feature} config={config} currentTier={userTier} optionsProActive={optionsProActive} />;
};

// ============================================
// UPGRADE PROMPT COMPONENT
// ============================================

export const UpgradePrompt = ({ feature, config, currentTier, optionsProActive }) => {
  const requiredTier = config?.minTier || 'premium';
  const isOptionsProFeature = requiredTier === 'options_pro';
  const colors = TIER_COLORS[requiredTier] || TIER_COLORS.premium;
  const Icon = config?.icon || Lock;

  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.5)',
      border: `1px solid ${colors.border}`,
      borderRadius: '1rem',
      padding: '3rem',
      textAlign: 'center',
      maxWidth: '500px',
      margin: '2rem auto'
    }}>
      {/* Icon */}
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: colors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 1.5rem'
      }}>
        <Icon size={40} color={colors.primary} />
      </div>

      {/* Title */}
      <h3 style={{
        fontSize: '1.5rem',
        fontWeight: 'bold',
        marginBottom: '0.75rem',
        color: colors.primary
      }}>
        {isOptionsProFeature ? 'Options Pro Feature' : `Unlock ${config?.name || 'This Feature'}`}
      </h3>

      {/* Description */}
      <p style={{
        color: '#94a3b8',
        fontSize: '1rem',
        marginBottom: '1.5rem',
        lineHeight: '1.6'
      }}>
        {isOptionsProFeature ? (
          <>
            {config?.name || feature} requires the{' '}
            <span style={{ color: colors.primary, fontWeight: '600' }}>
              🎯 Options Pro
            </span>{' '}
            subscription.
          </>
        ) : (
          <>
            {config?.name || feature} is available with{' '}
            <span style={{ color: colors.primary, fontWeight: '600' }}>
              {requiredTier === 'pro' ? '💎 Pro' : '👑 Premium'}
            </span>{' '}
            subscription.
          </>
        )}
      </p>

      {/* Benefits */}
      <div style={{
        background: 'rgba(51, 65, 85, 0.5)',
        borderRadius: '0.75rem',
        padding: '1rem',
        marginBottom: '1.5rem',
        textAlign: 'left'
      }}>
        <div style={{ fontSize: '0.875rem', color: '#cbd5e1', fontWeight: '600', marginBottom: '0.75rem' }}>
          {isOptionsProFeature ? '🎯 Options Pro includes:' : requiredTier === 'pro' ? '💎 Pro includes:' : '👑 Premium includes:'}
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#94a3b8', fontSize: '0.875rem', lineHeight: '1.8' }}>
          {isOptionsProFeature ? (
            <>
              <li>Options Chain Explorer</li>
              <li>Options Strategy Builder</li>
              <li>Options Screener</li>
              <li>IV Crush Predictor</li>
              <li>Greeks Analysis</li>
              <li>Options Flow & Unusual Activity</li>
            </>
          ) : requiredTier === 'pro' ? (
            <>
              <li>Unlimited stock lookups</li>
              <li>50 watchlist stocks</li>
              <li>ETF X-Ray & Compare tools</li>
              <li>Recession dashboard</li>
              <li>DCA Calculator</li>
            </>
          ) : (
            <>
              <li>Everything in Pro</li>
              <li>Congress & insider trades</li>
              <li>Whale watcher (13F filings)</li>
              <li>Advanced portfolio analytics</li>
              <li>Forex dashboard</li>
              <li>Unlimited AI questions</li>
            </>
          )}
        </ul>
      </div>

      {/* Price */}
      <div style={{ marginBottom: '1.5rem' }}>
        <span style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white' }}>
          ${isOptionsProFeature ? '80' : requiredTier === 'pro' ? '9.99' : '19.99'}
        </span>
        <span style={{ color: '#94a3b8', fontSize: '1rem' }}>/month</span>
      </div>

      {/* CTA Button */}
      <button
        onClick={() => {
          window.location.href = '/?tab=subscription';
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '1rem 2rem',
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary}cc 100%)`,
          border: 'none',
          borderRadius: '0.5rem',
          color: 'white',
          fontWeight: '600',
          fontSize: '1rem',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
      >
        {isOptionsProFeature ? <Target size={20} /> : <Zap size={20} />}
        {isOptionsProFeature ? 'Subscribe to Options Pro' : `Upgrade to ${requiredTier === 'pro' ? 'Pro' : 'Premium'}`}
        <ArrowRight size={20} />
      </button>

      {/* Trial info for Options Pro */}
      {isOptionsProFeature && (
        <p style={{ 
          marginTop: '1rem', 
          color: '#64748b', 
          fontSize: '0.875rem' 
        }}>
          7-day free trial included • Cancel anytime
        </p>
      )}

      {/* Current tier info */}
      <p style={{ 
        marginTop: '1rem', 
        color: '#64748b', 
        fontSize: '0.75rem' 
      }}>
        Your current plan: <span style={{ color: '#94a3b8', textTransform: 'capitalize' }}>{currentTier}</span>
        {optionsProActive && <span style={{ color: '#f97316' }}> + Options Pro</span>}
      </p>
    </div>
  );
};

export default FeatureGate;